import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import compose from 'lodash/fp/compose';
import lodash from 'lodash';
import { format, differenceInDays } from 'date-fns';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'hooks/useForm';
import LottieLoader from 'components/common/LottieLoader';
import {
  Input,
  Select,
  InputDatePicker,
  ButtonLink,
  Button,
  TransactionForm,
} from 'components/common/FormItems';
import {
  withTranslation,
  withCurrency,
  withPersonalInfo,
  withUserDefaultSpace,
  withCustomRouter,
  withBSPosition,
  withMeasurementUnit,
  withContract,
  withMsgInfo,
} from '../../../../hoc';
import {
  createIncomeTransaction as createAssetTransaction,
  getSingleIncomeTransaction,
  updateIncomeTransaction,
} from 'actions';
import { useFinancialCategory } from 'hooks/useFinancialCategory';
import { useReferenceCheckingAccount } from 'hooks/useReferenceCheckingAccount';
import { useFavoriteOrganisation } from 'hooks/useFavoriteOrganisation';
import { useAssetMasterData } from '../hooks/useAssetMasterData';
import { useOrgIndicator } from 'hooks/useOrgIndicator';
import { useAssetFinancialAccounts } from 'hooks/useAssetFinancialAccounts';
import { useCurrencyRate } from 'hooks/useCurrencyRate';
import { getDateDistance, formCurrencyConverter } from '../finance-utils';
import { ORG_INDICATOR_TYPES, orgIndicatorOptions } from '../../../../../constants';

// Accounts that may require quantity tracking
const QUANTITY_RELEVANT_ACCOUNTS = ['object', 'securit'];

// Accounts where currency is predefined from master data
const MASTER_DATA_ACCOUNTS = ['receive', 'object', 'realest', 'securit'];
const CASH_BANK_ACCOUNTS = ['cash', 'bank'];
const LIVE_BUSINESS_ERROR_KEYS = ['bankTransfer', 'cashBankCurrency', 'insufficientBalance', 'amount'];
const getTodayTransactionDate = () => format(new Date(), 'yyyy-MM-dd');

// Account category groups for Gain/Loss entries
const GAIN_CATEGORY_GROUP = 'income';
const LOSS_CATEGORY_GROUP = 'expense';
const EXCHANGE_GAIN_ACCOUNT_ID = 'capital';
const EXCHANGE_LOSS_ACCOUNT_ID = 'capitalexp';
const ALIGNED_FORM_ROW_WIDTH = 'calc(100% - 2.5rem)';


const Wrapper = styled.div``;

const StyledButtonLink = styled(ButtonLink)`
  span {
    text-transform: uppercase;
  }
`;

const DualInputWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;

  > div {
    flex: 1;
    min-width: 180px;
  }
`;

const AmountRowWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
`;

const TopAlignedField = styled.div`
  width: ${ALIGNED_FORM_ROW_WIDTH};
  max-width: ${ALIGNED_FORM_ROW_WIDTH};
  box-sizing: border-box;
`;

const AmountFieldPair = styled.div`
  flex: 1;
  min-width: 180px;
  display: flex;
  gap: 0.5rem;
  align-items: end;

  /* 
    To match the width of standard Input components (which have width: 98% and w-90),
    we apply the same width constraint to the pair container itself.
  */
  width: 98% !important;
  max-width: 98%;

  /* Target the outer wrapper of Input and Select components */
  > div {
    min-width: 0;
    margin-bottom: 0;
    padding-right: 0 !important;
  }

  > div:first-child {
    flex: 1;
  }

  > div:last-child {
    flex: 0 0 40%;
  }

  /* Target the internal InputWrapper and Select's wrapper div */
  > div > div {
    width: 100% !important;
    margin-bottom: 0;
  }

  /* Ensure form-group classes don't override our layout */
  .form-group {
    width: 100% !important;
    margin-bottom: 0;
  }

  /* Override Bootstrap width classes */
  .w-90,
  .w-100 {
    width: 100% !important;
  }
`;

const SingleAmountField = styled.div`
  flex: 1;
  min-width: 180px;
  display: flex;
  align-items: end;
  width: 98% !important;
  max-width: 98%;

  > div {
    width: 100% !important;
    min-width: 0;
    margin-bottom: 0;
    padding-right: 0 !important;
  }

  > div > div,
  .form-group,
  .w-90,
  .w-100 {
    width: 100% !important;
    margin-bottom: 0;
  }
`;

const AlignedFormRows = styled.div`
  width: ${ALIGNED_FORM_ROW_WIDTH};
  max-width: ${ALIGNED_FORM_ROW_WIDTH};

  > .form-group,
  > ${DualInputWrapper},
  > ${AmountRowWrapper} {
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box;
  }
`;

const StyledSelect = styled(Select)`
  > div {
    width: 100% !important;
  }
`;

const StyledInput = styled(Input)`
  > div {
    width: 100% !important;
  }
`;

const StyledInputDescription = styled(Input)`
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box;

  input {
    width: 100% !important;
  }
`;

const StyledInputDatePicker = styled(InputDatePicker)`
  width: 100% !important;
  max-width: 100% !important;
  box-sizing: border-box;

  > div {
    width: 100% !important;
  }
`;

const SectionTitle = styled.h5`
  margin-top: 2rem;
  margin-bottom: 1rem;
  text-decoration: underline;
  font-weight: 600;
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  border-radius: 4px;
  padding: 0.75rem 1rem;
  margin-top: 0.5rem;
  font-size: 0.875rem;
`;

const formatSelectOption = (id, label) => ({
  id: String(id),
  long_description_eng: label,
  long_description_deu: label,
});

const normalizeAmount = (value) => {
  if (value === '' || value === null || typeof value === 'undefined') {
    return '';
  }
  const parsed = parseFloat(value);
  if (Number.isNaN(parsed)) {
    return '';
  }
  return parsed.toFixed(2);
};

const getTransactionItemByLine = (transactionItems, lineNumber, dcIndicator) => {
  if (!Array.isArray(transactionItems)) {
    return {};
  }

  return (
    transactionItems.find((item) => Number(item.line) === Number(lineNumber)) ||
    transactionItems.find((item) => item.dc_indicator === dcIndicator) ||
    {}
  );
};

const getAssetShiftItems = (transactionItems, validAssetAccountIds) => {
  if (!Array.isArray(transactionItems) || transactionItems.length === 0) {
    return { debitItem: {}, creditItem: {} };
  }

  const assetItems = transactionItems.filter((item) =>
    validAssetAccountIds.has(item.financial_account),
  );

  if (assetItems.length >= 2) {
    return {
      debitItem:
        assetItems.find((item) => item.dc_indicator === 'D') ||
        getTransactionItemByLine(assetItems, 1, 'D'),
      creditItem:
        assetItems.find((item) => item.dc_indicator === 'C') ||
        getTransactionItemByLine(assetItems, 2, 'C'),
    };
  }

  return {
    debitItem: getTransactionItemByLine(transactionItems, 1, 'D'),
    creditItem: getTransactionItemByLine(transactionItems, 2, 'C'),
  };
};

const findClosestCurrencyRate = (selectedDate, allRates) => {
  if (!selectedDate || !allRates || allRates.length === 0) {
    return null;
  }

  const [selectedYear, selectedMonth] = selectedDate.split('-');
  const LAST_AVAILABLE_DATE = '2011-01-01';
  const LAST_FETCHED_MONTHLY_DATE = '2021-05-01';
  const PERIOD_BEFORE_DAILY_RATES = '2021-05-31';
  const PERIOD_OF_DAILY_RATES = '2021-06-01';

  if (selectedDate < LAST_AVAILABLE_DATE) {
    return null;
  }

  if (
    selectedDate >= LAST_FETCHED_MONTHLY_DATE &&
    selectedDate < PERIOD_BEFORE_DAILY_RATES
  ) {
    return allRates.find((rate) => rate.date === LAST_FETCHED_MONTHLY_DATE);
  }

  if (selectedDate >= PERIOD_OF_DAILY_RATES) {
    const exactRate = allRates.find((rate) => rate.date === selectedDate);
    if (exactRate) {
      return exactRate;
    }

    const previousRates = allRates.filter((rate) => rate.date <= selectedDate);
    return previousRates.pop() || null;
  }

  const selectedDateObj = new Date(selectedDate);
  const month = parseInt(selectedMonth, 10);
  const year = parseInt(selectedYear, 10);
  const FIRST_DAY = '01';

  if (month === 1) {
    return getDateDistance(
      differenceInDays(selectedDateObj, new Date(`${year - 1}-12-${FIRST_DAY}`)),
      differenceInDays(new Date(`${year}-02-${FIRST_DAY}`), selectedDateObj),
      allRates,
      `${year - 1}-12-${FIRST_DAY}`,
      `${year}-02-${FIRST_DAY}`,
    );
  }

  if (month === 12) {
    return getDateDistance(
      differenceInDays(selectedDateObj, new Date(`${year}-11-${FIRST_DAY}`)),
      differenceInDays(new Date(`${year}-01-${FIRST_DAY}`), selectedDateObj),
      allRates,
      `${year}-11-${FIRST_DAY}`,
      `${year}-01-${FIRST_DAY}`,
    );
  }

  const nextMonth = String(month + 1).padStart(2, '0');
  return getDateDistance(
    differenceInDays(selectedDateObj, new Date(`${year}-${selectedMonth}-${FIRST_DAY}`)),
    differenceInDays(new Date(`${year}-${nextMonth}-${FIRST_DAY}`), selectedDateObj),
    allRates,
    `${year}-${selectedMonth}-${FIRST_DAY}`,
    `${year}-${nextMonth}-${FIRST_DAY}`,
  );
};

const initialValues = {
  // Header fields
  contract: '',
  contract_item: '',
  date: getTodayTransactionDate(),
  description: '',
  person: '',
  partner_org_ind: '',
  partner: '',
  // Debit (D+) line
  debit_financial_account: 'cash',
  debit_position_link: '',
  debit_amount: '',
  debit_currency: '',
  debit_amount_sc: '',
  // Credit (C) line
  credit_financial_account: 'bank',
  credit_position_link: '',
  credit_amount: '',
  credit_currency: '',
  credit_amount_sc: '',
  // Quantity fields (for securities/objects)
  quantity: '',
  quantity_line: '',
  measurement_unit: '',
  id: null, // Add ID field to initialValues
};

const validationSchema = {
  date: 'required',
  description: 'required',
  person: 'required',
  debit_financial_account: 'required',
  credit_financial_account: 'required',
  debit_amount: 'required',
  credit_amount: 'required',
};

const AssetShiftForm = (props) => {
  const {
    tr,
    trObj = () => {},
    personalInfoOptions,
    currencyLocaleOptions,
    measurementLocaleOptions = [],
    userDefaultSpace,
    gotoRoute,
    basePath,
    bsPosition = [],
    contractInfoOptions = [],
    setMsgInfo = () => {},
    routeParams,
    updateMode,
    reverseMode,
    detailMode,
    location,
  } = props;

  const dispatch = useDispatch();
  const { currencyRate } = useCurrencyRate();
  const favoriteOrganisation = useFavoriteOrganisation();
  const referenceCheckingAccount = useReferenceCheckingAccount();
  // Removed duplicate declarations
  const persons = useSelector((state) => state.personalInfo.data);
  const { item } = useSelector((state) => state.transaction);
  const { id: transactionId } = routeParams;



  const { financialCategoryOptions } = useFinancialCategory();
  const { assetAccounts, loading: assetAccountsLoading } = useAssetFinancialAccounts();
  const { objects, securities, realEstates } = useAssetMasterData();
  const { organisationOrPersonOptions, handleOrgIndicatorChange } = useOrgIndicator({
    currentOrgIndicator: ORG_INDICATOR_TYPES.ORG,
  });

  const [formLoading, setFormLoading] = useState(false);
  const [hasLoadedItem, setHasLoadedItem] = useState(false);

  const [businessErrors, setBusinessErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Use refs to track previous values and prevent infinite loops
  const prevDebitPositionRef = useRef('');
  const prevCreditPositionRef = useRef('');
  const isInitializedRef = useRef(false);

  const defaultCurrency = userDefaultSpace?.space_detail?.currency || 'EUR';

  const {
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    values,
    setValues,
    errors,
  } = useForm({
    validationSchema,
    initialValues,
    onSubmit: async (formValues) => {
      if (isSubmitting) return;

      const newErrors = validateBusinessRules(formValues);
      setBusinessErrors(newErrors);

      if (Object.keys(newErrors).length) {
        return;
      }

      setIsSubmitting(true);
      try {
        const payload = buildPayload(formValues);

        if (formValues.id) {
          // Update mode
          const err = await dispatch(updateIncomeTransaction(formValues.id, payload));
          if (!err) {
            setMsgInfo({
              success: true,
              msg: [tr('Asset shift transaction updated successfully')],
            });
            gotoRoute(basePath);
          }
        } else {
          // Create mode
          const err = await dispatch(createAssetTransaction(payload));
          if (!err) {
            setMsgInfo({
              success: true,
              msg: [tr('Asset shift transaction created successfully')],
            });
            gotoRoute(basePath);
          }
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Fetch transaction data
  useEffect(() => {
    const fetchData = async () => {
      if (transactionId) {
        setHasLoadedItem(false);
        setFormLoading(true);
        try {
          await dispatch(getSingleIncomeTransaction(transactionId));

        } catch (error) {
          console.error('[AssetShift] Fetch error:', error);
        } finally {
          setFormLoading(false);
        }
      }
    };
    fetchData();
  }, [transactionId, dispatch]);

  // ============================================
  // LOOKUP MAPS
  // ============================================

  const accountCategoryMap = useMemo(() => {
    const map = {};
    financialCategoryOptions.forEach((item) => {
      map[item.id] = item.acc_cat_group;
    });
    return map;
  }, [financialCategoryOptions]);

  // Use dynamically fetched asset accounts instead of hardcoded list
  const financialAccountLocaleOptions = useMemo(
    () => assetAccounts.map((item) => ({ ...item })),
    [assetAccounts],
  );

  // Create a set of valid asset account IDs for validation
  const validAssetAccountIds = useMemo(
    () => new Set(assetAccounts.map((item) => item.id)),
    [assetAccounts],
  );

  // Get first available income and expense accounts for gain/loss entries
  const defaultGainAccount = useMemo(() => {
    const exchangeGainAccount = financialCategoryOptions.find(
      (item) => item.id === EXCHANGE_GAIN_ACCOUNT_ID && item.acc_cat_group === GAIN_CATEGORY_GROUP,
    );
    if (exchangeGainAccount) return exchangeGainAccount;

    const incomeAccounts = financialCategoryOptions.filter(
      (item) => item.acc_cat_group === GAIN_CATEGORY_GROUP
    );
    return incomeAccounts.length > 0 ? incomeAccounts[0] : null;
  }, [financialCategoryOptions]);

  const defaultLossAccount = useMemo(() => {
    const exchangeLossAccount = financialCategoryOptions.find(
      (item) => item.id === EXCHANGE_LOSS_ACCOUNT_ID && item.acc_cat_group === LOSS_CATEGORY_GROUP,
    );
    if (exchangeLossAccount) return exchangeLossAccount;

    const expenseAccounts = financialCategoryOptions.filter(
      (item) => item.acc_cat_group === LOSS_CATEGORY_GROUP
    );
    return expenseAccounts.length > 0 ? expenseAccounts[0] : null;
  }, [financialCategoryOptions]);

  const bankAccountMap = useMemo(() => {
    const map = new Map();
    referenceCheckingAccount.forEach((account) => {
      map.set(account.account_no, account);
    });
    return map;
  }, [referenceCheckingAccount]);

  const objectMap = useMemo(() => {
    const map = new Map();
    objects.forEach((item) => map.set(String(item.id), item));
    return map;
  }, [objects]);

  const securityMap = useMemo(() => {
    const map = new Map();
    securities.forEach((item) => map.set(String(item.id), item));
    return map;
  }, [securities]);

  const realEstateMap = useMemo(() => {
    const map = new Map();
    realEstates.forEach((item) => map.set(String(item.id), item));
    return map;
  }, [realEstates]);

  const bsPositionMap = useMemo(() => {
    const map = new Map();
    (bsPosition || []).forEach((position) => {
      const key = `${position.financial_account}_${position.position_link || position.currency || ''}`;
      map.set(key, position);
    });
    return map;
  }, [bsPosition]);

  // ============================================
  // HELPER FUNCTIONS
  // ============================================

  const getMeasurementUnitLabel = useCallback(
    (measurementUnitId) => {
      if (!measurementUnitId) return '';
      const unit = measurementLocaleOptions.find(
        (item) => String(item.id) === String(measurementUnitId),
      );
      return unit?.long_description_eng || unit?.name || '';
    },
    [measurementLocaleOptions],
  );

  const resolveMasterRecord = useCallback(
    (accountId, positionLink) => {
      if (!positionLink) return null;
      if (accountId === 'object') {
        return objectMap.get(String(positionLink));
      }
      if (accountId === 'securit') {
        return securityMap.get(String(positionLink));
      }
      if (accountId === 'realest') {
        return realEstateMap.get(String(positionLink));
      }
      return null;
    },
    [objectMap, realEstateMap, securityMap],
  );

  const getBsPosition = useCallback(
    (accountId, positionLink, currencyValue) => {
      const key = `${accountId}_${positionLink || currencyValue || ''}`;
      return bsPositionMap.get(key);
    },
    [bsPositionMap],
  );

  const getAvailableBalance = useCallback(
    (accountId, positionLink, currencyValue) => {
      const position = getBsPosition(accountId, positionLink, currencyValue);
      if (!position || position.actual_value === null || typeof position.actual_value === 'undefined') {
        return 0;
      }

      const parsedActualValue = parseFloat(position.actual_value);
      return Number.isNaN(parsedActualValue) ? 0 : parsedActualValue;
    },
    [getBsPosition],
  );

  const getBankAccountCurrency = useCallback(
    (positionLink) => bankAccountMap.get(positionLink)?.currency || '',
    [bankAccountMap],
  );

  const getLockedCreditCashCurrency = useCallback(
    (formValues) => {
      if (
        formValues.credit_financial_account !== 'cash' ||
        formValues.debit_financial_account !== 'bank'
      ) {
        return '';
      }

      return getBankAccountCurrency(formValues.debit_position_link);
    },
    [getBankAccountCurrency],
  );

  const allCurrencyRate = useMemo(
    () => lodash.compact(Object.keys(currencyRate || {}).map((key) => currencyRate[key])),
    [currencyRate],
  );

  const getConvertedAmountSc = useCallback(
    (amountValue, currencyValue, transactionDate) => {
      const effectiveTransactionDate = transactionDate || getTodayTransactionDate();
      if (
        amountValue === '' ||
        amountValue === null ||
        typeof amountValue === 'undefined' ||
        !currencyValue ||
        !defaultCurrency ||
        allCurrencyRate.length === 0
      ) {
        return '';
      }

      const closestRate = findClosestCurrencyRate(effectiveTransactionDate, allCurrencyRate);
      if (!closestRate) {
        return '';
      }

      const converted = formCurrencyConverter(
        amountValue,
        closestRate,
        defaultCurrency,
        { currency: currencyValue },
      );

      const roundedConverted = parseFloat(
        (Math.round(parseFloat(converted) * 100) / 100).toFixed(2),
      );

      if (Number.isNaN(roundedConverted)) {
        return '';
      }

      return roundedConverted.toFixed(2);
    },
    [allCurrencyRate, defaultCurrency],
  );

  // ============================================
  // DERIVED STATE (computed from values)
  // ============================================

  // Determine if currency should be locked for a line
  const isCurrencyLocked = useCallback(
    (line) => {
      const accountId = values[`${line}_financial_account`];
      const otherLine = line === 'debit' ? 'credit' : 'debit';
      const otherAccountId = values[`${otherLine}_financial_account`];

      // Bank always locks currency
      if (accountId === 'bank') return true;

      // Master data accounts lock currency
      if (MASTER_DATA_ACCOUNTS.includes(accountId)) return true;

      // Cash(C) + Bank(D+): Cash must match bank currency
      if (
        line === 'credit' &&
        accountId === 'cash' &&
        otherAccountId === 'bank' &&
        values[`${otherLine}_position_link`]
      ) {
        return true;
      }

      return false;
    },
    [values],
  );

  // Determine which line's amount should be locked/mirrored
  const amountLockState = useMemo(() => {
    const { debit_financial_account, credit_financial_account } = values;

    // Rule: Cash(C) + Bank(D+) -> Bank amount mirrors Cash
    if (credit_financial_account === 'cash' && debit_financial_account === 'bank') {
      return { debitLocked: true, creditLocked: false, mirrorSource: 'credit' };
    }

    // Rule: Master data on Credit, Cash/Bank on Debit -> Credit mirrors Debit
    if (
      MASTER_DATA_ACCOUNTS.includes(credit_financial_account) &&
      ['cash', 'bank'].includes(debit_financial_account)
    ) {
      // Exception: Receivable <-> Object exchange - Receivable is editable
      if (credit_financial_account === 'receive' && debit_financial_account === 'object') {
        return { debitLocked: false, creditLocked: false, mirrorSource: null };
      }
      return { debitLocked: false, creditLocked: true, mirrorSource: 'debit' };
    }

    // Rule: Master data on Debit, Cash/Bank on Credit -> Debit mirrors Credit
    if (
      MASTER_DATA_ACCOUNTS.includes(debit_financial_account) &&
      ['cash', 'bank'].includes(credit_financial_account)
    ) {
      // Exception: Receivable <-> Object exchange - Receivable is editable
      if (debit_financial_account === 'receive' && credit_financial_account === 'object') {
        return { debitLocked: false, creditLocked: false, mirrorSource: null };
      }
      return { debitLocked: true, creditLocked: false, mirrorSource: 'credit' };
    }

    // Default: Both amounts are editable
    return { debitLocked: false, creditLocked: false, mirrorSource: null };
  }, [values]);

  // Determine which line should show quantity fields
  const quantityLineInfo = useMemo(() => {
    const { debit_financial_account, credit_financial_account, debit_position_link, credit_position_link } = values;

    // Check debit line
    if (QUANTITY_RELEVANT_ACCOUNTS.includes(debit_financial_account) && debit_position_link) {
      const master = debit_financial_account === 'object'
        ? objectMap.get(String(debit_position_link))
        : securityMap.get(String(debit_position_link));
      // Securities always have quantity, objects check quantity_relevant flag
      if (debit_financial_account === 'securit' || master?.quantity_relevant) {
        return { line: 'debit', measurementUnit: master?.measurement_unit || null };
      }
    }

    // Check credit line
    if (QUANTITY_RELEVANT_ACCOUNTS.includes(credit_financial_account) && credit_position_link) {
      const master = credit_financial_account === 'object'
        ? objectMap.get(String(credit_position_link))
        : securityMap.get(String(credit_position_link));
      if (credit_financial_account === 'securit' || master?.quantity_relevant) {
        return { line: 'credit', measurementUnit: master?.measurement_unit || null };
      }
    }

    return { line: null, measurementUnit: null };
  }, [values, objectMap, securityMap]);

  // ============================================
  // POSITION OPTIONS BUILDERS
  // ============================================

  const buildPositionOptions = useCallback(
    (accountId) => {
      if (accountId === 'cash') {
        return currencyLocaleOptions;
      }

      if (accountId === 'bank') {
        return referenceCheckingAccount.map((account) =>
          formatSelectOption(
            account.account_no,
            `${account.BIC_data?.bank_name || 'Bank'} - ${account.account_no}`,
          ),
        );
      }

      if (accountId === 'receive') {
        const personOptions = persons.map((person) =>
          formatSelectOption(
            `P_${person.id}`,
            `${person.first_name || ''} ${person.last_name || ''}`.trim(),
          ),
        );
        const orgOptions = favoriteOrganisation.map((org) =>
          formatSelectOption(`O_${org.id}`, org.name),
        );
        return [...personOptions, ...orgOptions];
      }

      if (accountId === 'object') {
        return objects.map((item) =>
          formatSelectOption(item.id, item.description || `Object #${item.id}`),
        );
      }

      if (accountId === 'realest') {
        return realEstates.map((item) =>
          formatSelectOption(item.id, item.description || `RealEstate #${item.id}`),
        );
      }

      if (accountId === 'securit') {
        return securities.map((item) =>
          formatSelectOption(
            item.id,
            `${item.description || 'Security'} - ${item.isin || item.id}`,
          ),
        );
      }

      return [];
    },
    [
      currencyLocaleOptions,
      referenceCheckingAccount,
      persons,
      favoriteOrganisation,
      objects,
      realEstates,
      securities,
    ],
  );

  const debitPositionOptions = useMemo(
    () => buildPositionOptions(values.debit_financial_account),
    [buildPositionOptions, values.debit_financial_account],
  );

  const creditPositionOptions = useMemo(
    () => buildPositionOptions(values.credit_financial_account),
    [buildPositionOptions, values.credit_financial_account],
  );

  const contractOptions = useMemo(
    () =>
      (contractInfoOptions || []).map((contract) =>
        formatSelectOption(contract.id, contract.name),
      ),
    [contractInfoOptions],
  );

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    if (!(item && item.id) || hasLoadedItem || String(item.id) !== String(transactionId)) {
      return;
    }

    const transactionItems = item.transaction_item || [];
    if (assetAccountsLoading || validAssetAccountIds.size === 0) {
      return;
    }
    const hasBankItems = transactionItems.some(
      (transactionItem) =>
        transactionItem.financial_account === 'bank' && transactionItem.position_link,
    );

    // Bank lines use account numbers as select values, so wait for that lookup data.
    if (hasBankItems && referenceCheckingAccount.length === 0) {
      return;
    }

    setHasLoadedItem(true);

    const { debitItem, creditItem } = getAssetShiftItems(
      transactionItems,
      validAssetAccountIds,
    );

    const newValues = { ...initialValues, ...item };

    if (debitItem.person) {
      newValues.person = String(debitItem.person);
    } else if (creditItem.person) {
      newValues.person = String(creditItem.person);
    }

    if (item.partner_org_ind) {
      newValues.partner_org_ind = item.partner_org_ind;
      handleOrgIndicatorChange(item.partner_org_ind);
    }
    if (item.partner) {
      newValues.partner = String(item.partner);
    } else if (item.partner_detail?.id) {
      newValues.partner = String(item.partner_detail.id);
    }

    if (item.contract) newValues.contract = String(item.contract);
    if (item.description) newValues.description = item.description;
    if (item.date) newValues.date = item.date;

    newValues.debit_financial_account = debitItem.financial_account || '';
    newValues.debit_currency = debitItem.currency || defaultCurrency;
    newValues.debit_amount = debitItem.amount || '';
    newValues.debit_amount_sc = debitItem.amount_sc || '';

    if (['receive', 'payable'].includes(debitItem.financial_account)) {
      let positionLinkId = debitItem.position_link;
      if (positionLinkId) {
        positionLinkId = String(positionLinkId);
        if (debitItem.bpos_org_indicator === 'P') {
          newValues.debit_position_link = `P_${positionLinkId}`;
        } else if (debitItem.bpos_org_indicator === 'O') {
          newValues.debit_position_link = `O_${positionLinkId}`;
        } else {
          newValues.debit_position_link = positionLinkId;
        }
      }
    } else if (debitItem.financial_account === 'cash') {
      newValues.debit_position_link = debitItem.currency || debitItem.amount;
    } else if (debitItem.financial_account === 'bank') {
      if (debitItem.position_link) {
        const isAccountNumber = referenceCheckingAccount.some(
          (acc) => acc.account_no === String(debitItem.position_link),
        );
        if (isAccountNumber) {
          newValues.debit_position_link = String(debitItem.position_link);
        } else {
          const matchingBankAccount = referenceCheckingAccount.find(
            (acc) => acc.id === debitItem.position_link,
          );
          newValues.debit_position_link = matchingBankAccount
            ? matchingBankAccount.account_no
            : String(debitItem.position_link);
        }
      }
    } else {
      newValues.debit_position_link = debitItem.position_link ? String(debitItem.position_link) : '';
    }

    newValues.credit_financial_account = creditItem.financial_account || '';
    newValues.credit_currency = creditItem.currency || defaultCurrency;
    newValues.credit_amount = creditItem.amount || '';
    newValues.credit_amount_sc = creditItem.amount_sc || '';

    if (['receive', 'payable'].includes(creditItem.financial_account)) {
      let positionLinkId = creditItem.position_link;
      if (positionLinkId) {
        positionLinkId = String(positionLinkId);
        if (creditItem.bpos_org_indicator === 'P') {
          newValues.credit_position_link = `P_${positionLinkId}`;
        } else if (creditItem.bpos_org_indicator === 'O') {
          newValues.credit_position_link = `O_${positionLinkId}`;
        } else {
          newValues.credit_position_link = positionLinkId;
        }
      }
    } else if (creditItem.financial_account === 'cash') {
      newValues.credit_position_link = creditItem.currency || creditItem.amount;
    } else if (creditItem.financial_account === 'bank') {
      if (creditItem.position_link) {
        const isAccountNumber = referenceCheckingAccount.some(
          (acc) => acc.account_no === String(creditItem.position_link),
        );
        if (isAccountNumber) {
          newValues.credit_position_link = String(creditItem.position_link);
        } else {
          const matchingBankAccount = referenceCheckingAccount.find(
            (acc) => acc.id === creditItem.position_link,
          );
          newValues.credit_position_link = matchingBankAccount
            ? matchingBankAccount.account_no
            : String(creditItem.position_link);
        }
      }
    } else {
      newValues.credit_position_link = creditItem.position_link ? String(creditItem.position_link) : '';
    }

    const quantityItem = transactionItems.find((transactionItem) => transactionItem.quantity);
    if (quantityItem) {
      newValues.quantity = quantityItem.quantity;
      newValues.measurement_unit = quantityItem.measurement_unit;
    }

    setValues(newValues);
  }, [
    item,
    transactionId,
    hasLoadedItem,
    setValues,
    defaultCurrency,
    handleOrgIndicatorChange,
    referenceCheckingAccount,
    assetAccountsLoading,
    validAssetAccountIds,
  ]);

  // Initialize default currency (run once)
  useEffect(() => {
    if (!isInitializedRef.current && defaultCurrency) {
      isInitializedRef.current = true;
      setValues((prev) => ({
        ...prev,
        debit_currency: prev.debit_currency || defaultCurrency,
        credit_currency: prev.credit_currency || defaultCurrency,
      }));
    }
  }, [defaultCurrency, setValues]);

  useEffect(() => {
    if (values.debit_financial_account !== 'cash') {
      return;
    }

    const debitCurrency = values.debit_currency || defaultCurrency;
    if (values.debit_position_link !== debitCurrency) {
      setValues((prev) => ({
        ...prev,
        debit_position_link: debitCurrency,
      }));
    }
  }, [
    values.debit_financial_account,
    values.debit_currency,
    values.debit_position_link,
    defaultCurrency,
    setValues,
  ]);

  useEffect(() => {
    if (values.credit_financial_account !== 'cash') {
      return;
    }

    const lockedCreditCurrency = getLockedCreditCashCurrency(values);
    let nextCreditCurrency = values.credit_currency || defaultCurrency;
    if (lockedCreditCurrency) {
      nextCreditCurrency = lockedCreditCurrency;
    }

    if (
      values.credit_currency !== nextCreditCurrency ||
      values.credit_position_link !== nextCreditCurrency
    ) {
      setValues((prev) => ({
        ...prev,
        credit_currency: nextCreditCurrency,
        credit_position_link: nextCreditCurrency,
      }));
    }
  }, [
    values.credit_financial_account,
    values.credit_currency,
    values.credit_position_link,
    values.debit_financial_account,
    values.debit_position_link,
    defaultCurrency,
    getLockedCreditCashCurrency,
    setValues,
  ]);

  useEffect(() => {
    if (values.date) {
      return;
    }

    const fallbackDate = getTodayTransactionDate();
    setValues((prev) => (
      prev.date ? prev : { ...prev, date: fallbackDate }
    ));
  }, [values.date, setValues]);

  useEffect(() => {
    const nextDebitAmountSc = getConvertedAmountSc(
      values.debit_amount,
      values.debit_currency,
      values.date,
    );
    const nextCreditAmountSc = getConvertedAmountSc(
      values.credit_amount,
      values.credit_currency,
      values.date,
    );

    if (
      values.debit_amount_sc !== nextDebitAmountSc ||
      values.credit_amount_sc !== nextCreditAmountSc
    ) {
      setValues((prev) => ({
        ...prev,
        debit_amount_sc: nextDebitAmountSc,
        credit_amount_sc: nextCreditAmountSc,
      }));
    }
  }, [
    values.date,
    values.debit_amount,
    values.debit_currency,
    values.credit_amount,
    values.credit_currency,
    values.debit_amount_sc,
    values.credit_amount_sc,
    getConvertedAmountSc,
    setValues,
  ]);

  // Update currency when debit bank account is selected
  useEffect(() => {
    if (
      values.debit_financial_account === 'bank' &&
      values.debit_position_link &&
      values.debit_position_link !== prevDebitPositionRef.current
    ) {
      prevDebitPositionRef.current = values.debit_position_link;
      const bank = bankAccountMap.get(values.debit_position_link);
      if (bank?.currency && bank.currency !== values.debit_currency) {
        setValues((prev) => ({ ...prev, debit_currency: bank.currency }));
      }
    }
  }, [values.debit_financial_account, values.debit_position_link, values.debit_currency, bankAccountMap, setValues]);

  // Update currency when credit bank account is selected
  useEffect(() => {
    if (
      values.credit_financial_account === 'bank' &&
      values.credit_position_link &&
      values.credit_position_link !== prevCreditPositionRef.current
    ) {
      prevCreditPositionRef.current = values.credit_position_link;
      const bank = bankAccountMap.get(values.credit_position_link);
      if (bank?.currency && bank.currency !== values.credit_currency) {
        setValues((prev) => ({ ...prev, credit_currency: bank.currency }));
      }
    }
  }, [values.credit_financial_account, values.credit_position_link, values.credit_currency, bankAccountMap, setValues]);

  // ============================================
  // EVENT HANDLERS
  // ============================================

  const handleAccountChange = useCallback(
    (line, event) => {
      // Reset the position ref when account changes
      if (line === 'debit') {
        prevDebitPositionRef.current = '';
      } else {
        prevCreditPositionRef.current = '';
      }

      handleChange(event);
      // Clear position and amount when account type changes
      setValues((prev) => ({
        ...prev,
        [`${line}_position_link`]: '',
        [`${line}_amount`]: '',
        [`${line}_currency`]: defaultCurrency,
        [`${line}_amount_sc`]: '',
      }));
    },
    [handleChange, setValues, defaultCurrency],
  );

  const handlePositionChange = useCallback(
    (line, event) => {
      const newPosition = event.target.value;
      const accountId = values[`${line}_financial_account`];

      handleChange(event);

      // Update currency based on the selected position
      if (accountId === 'bank') {
        const bank = bankAccountMap.get(newPosition);
        if (bank?.currency) {
          setValues((prev) => ({
            ...prev,
            [`${line}_currency`]: bank.currency,
            ...(line === 'debit' && prev.credit_financial_account === 'cash'
              ? {
                  credit_currency: bank.currency,
                  credit_position_link: bank.currency,
                }
              : {}),
          }));
        }
      } else if (MASTER_DATA_ACCOUNTS.includes(accountId)) {
        const master = resolveMasterRecord(accountId, newPosition);
        if (master?.currency) {
          setValues((prev) => ({
            ...prev,
            [`${line}_currency`]: master.currency,
          }));
        }
      }
    },
    [handleChange, values, bankAccountMap, resolveMasterRecord, setValues],
  );

  const handleCashPositionChange = useCallback(
    (line, event) => {
      const newCurrency = event.target.value;
      setValues((prev) => ({
        ...prev,
        [`${line}_position_link`]: newCurrency,
        [`${line}_currency`]: newCurrency,
      }));
    },
    [setValues],
  );

  const handleCurrencyChange = useCallback(
    (line, event) => {
      if (values[`${line}_financial_account`] === 'cash') {
        handleCashPositionChange(line, event);
        return;
      }

      handleChange(event);
    },
    [values, handleCashPositionChange, handleChange],
  );

  const handleAmountChange = useCallback(
    (line, event) => {
      handleChange(event);

      // If this line is the mirror source, also update the target
      if (amountLockState.mirrorSource === line) {
        const targetLine = line === 'debit' ? 'credit' : 'debit';
        setValues((prev) => ({
          ...prev,
          [`${targetLine}_amount`]: event.target.value,
        }));
      }
    },
    [handleChange, amountLockState.mirrorSource, setValues],
  );

  const handlePartnerOrgIndChange = useCallback(
    (event) => {
      handleChange(event);
      handleOrgIndicatorChange(event.target.value);
      // Clear partner when indicator changes
      setValues((prev) => ({ ...prev, partner: '' }));
    },
    [handleChange, handleOrgIndicatorChange, setValues],
  );

  // ============================================
  // VALIDATION
  // ============================================

  const validateBusinessRules = useCallback((formValues, { liveValidation = false } = {}) => {
    const newErrors = {};
    const hasDebitAmount = formValues.debit_amount !== '' && formValues.debit_amount !== null;
    const hasCreditAmount = formValues.credit_amount !== '' && formValues.credit_amount !== null;
    const debitAmount = hasDebitAmount ? parseFloat(formValues.debit_amount) : 0;
    const creditAmount = hasCreditAmount ? parseFloat(formValues.credit_amount) : 0;
    const lockedCreditCurrency = getLockedCreditCashCurrency(formValues);
    const effectiveCreditCurrency = lockedCreditCurrency || formValues.credit_currency;

    // Rule: Bank-to-Bank with different currencies is not allowed
    if (
      formValues.debit_financial_account === 'bank' &&
      formValues.credit_financial_account === 'bank'
    ) {
      const debitBank = bankAccountMap.get(formValues.debit_position_link);
      const creditBank = bankAccountMap.get(formValues.credit_position_link);
      if (debitBank && creditBank && debitBank.currency !== creditBank.currency) {
        newErrors.bankTransfer = tr(
          'Transfers between bank accounts with different currencies are not allowed',
        );
      }
    }

    // Rule: Cash(C) + Bank(D+) - Cash currency must match bank currency
    if (
      formValues.credit_financial_account === 'cash' &&
      formValues.debit_financial_account === 'bank'
    ) {
      if (lockedCreditCurrency && lockedCreditCurrency !== formValues.credit_currency) {
        newErrors.cashBankCurrency = tr(
          'When depositing cash to bank, cash currency must match bank currency',
        );
      }
    }

    // Rule: Check balance coverage on Credit line
    if (
      hasCreditAmount &&
      !Number.isNaN(creditAmount) &&
      (formValues.credit_position_link || formValues.credit_financial_account === 'cash')
    ) {
      const positionKey = formValues.credit_financial_account === 'cash'
        ? effectiveCreditCurrency
        : formValues.credit_position_link;

      const availableBalance = getAvailableBalance(
        formValues.credit_financial_account,
        positionKey,
        effectiveCreditCurrency,
      );

      if (availableBalance !== null && creditAmount > availableBalance) {
        newErrors.insufficientBalance = `${tr('Insufficient balance')}. ${tr(
          'Available',
        )}: ${availableBalance.toFixed(2)}, ${tr('Required')}: ${creditAmount.toFixed(2)}`;
      }
    }

    // Live validation only shows dynamic business rules while the user edits.
    if (liveValidation) {
      if (
        (hasDebitAmount && Number.isNaN(debitAmount)) ||
        (hasCreditAmount && Number.isNaN(creditAmount))
      ) {
        newErrors.amount = tr('Amounts must be valid numbers');
      }
      return newErrors;
    }

    // Rule: Quantity required for securities/objects with quantity_relevant
    if (quantityLineInfo.line) {
      if (!formValues.quantity || parseFloat(formValues.quantity) <= 0) {
        newErrors.quantity = tr('Quantity is required for the selected asset type');
      }
    }

    // Rule: Validate amounts are numbers
    if (Number.isNaN(debitAmount) || Number.isNaN(creditAmount)) {
      newErrors.amount = tr('Amounts must be valid numbers');
    }

    // Rule: Position link required for non-cash accounts
    if (formValues.debit_financial_account !== 'cash' && !formValues.debit_position_link) {
      newErrors.debitPosition = tr('Position is required');
    }
    if (formValues.credit_financial_account !== 'cash' && !formValues.credit_position_link) {
      newErrors.creditPosition = tr('Position is required');
    }

    return newErrors;
  }, [bankAccountMap, getAvailableBalance, getLockedCreditCashCurrency, quantityLineInfo.line, tr]);

  useEffect(() => {
    if (!Object.keys(touched).length) {
      return;
    }

    const liveErrors = validateBusinessRules(values, { liveValidation: true });
    setBusinessErrors((prevErrors) => {
      const nextErrors = { ...prevErrors };

      LIVE_BUSINESS_ERROR_KEYS.forEach((key) => {
        if (liveErrors[key]) {
          nextErrors[key] = liveErrors[key];
        } else {
          delete nextErrors[key];
        }
      });

      return lodash.isEqual(prevErrors, nextErrors) ? prevErrors : nextErrors;
    });
  }, [touched, validateBusinessRules, values]);

  // ============================================
  // PAYLOAD BUILDER
  // ============================================

  const buildPayload = useCallback((formValues) => {
    const debitAmount = normalizeAmount(formValues.debit_amount);
    const creditAmount = normalizeAmount(formValues.credit_amount);
    const debitAmountSc = normalizeAmount(formValues.debit_amount_sc) || debitAmount;
    const creditAmountSc = normalizeAmount(formValues.credit_amount_sc) || creditAmount;
    const lockedCreditCurrency = getLockedCreditCashCurrency(formValues);
    const effectiveCreditCurrency = lockedCreditCurrency || formValues.credit_currency;

    // Build debit line
    const debitLine = {
      amount: debitAmount,
      amount_sc: debitAmountSc,
      financial_account: formValues.debit_financial_account,
      dc_indicator: 'D',
      currency: formValues.debit_currency,
      account_category: accountCategoryMap[formValues.debit_financial_account] || 'asset',
      line: 1,
      position_link:
        formValues.debit_financial_account === 'cash'
          ? formValues.debit_currency
          : formValues.debit_position_link,
      person: formValues.person,
    };

    // Build credit line
    const creditLine = {
      amount: creditAmount,
      amount_sc: creditAmountSc,
      financial_account: formValues.credit_financial_account,
      dc_indicator: 'C',
      currency: effectiveCreditCurrency,
      account_category: accountCategoryMap[formValues.credit_financial_account] || 'asset',
      line: 2,
      position_link:
        formValues.credit_financial_account === 'cash'
          ? effectiveCreditCurrency
          : formValues.credit_position_link,
      person: formValues.person,
    };

    // Add quantity to the relevant line
    if (quantityLineInfo.line === 'debit' && formValues.quantity) {
      debitLine.quantity = formValues.quantity;
      debitLine.measurement_unit = quantityLineInfo.measurementUnit || null;
    }
    if (quantityLineInfo.line === 'credit' && formValues.quantity) {
      creditLine.quantity = formValues.quantity;
      creditLine.measurement_unit = quantityLineInfo.measurementUnit || null;
    }

    const transactionItems = [debitLine, creditLine];

    const shouldCreateExchangeDifference =
      accountCategoryMap[formValues.debit_financial_account] === 'asset' &&
      accountCategoryMap[formValues.credit_financial_account] === 'asset';

    if (shouldCreateExchangeDifference && debitAmountSc && creditAmountSc) {
      const debitValueSc = parseFloat(debitAmountSc);
      const creditValueSc = parseFloat(creditAmountSc);

      if (!Number.isNaN(debitValueSc) && !Number.isNaN(creditValueSc)) {
        const difference = Math.abs(debitValueSc - creditValueSc).toFixed(2);

        if (difference !== '0.00') {
          const isGain = debitValueSc > creditValueSc;
          const gainLossAccount = isGain ? defaultGainAccount : defaultLossAccount;

          if (gainLossAccount) {
            transactionItems.push({
              amount: '0.00',
              amount_sc: difference,
              financial_account: gainLossAccount.id,
              account_category: gainLossAccount.acc_cat_group,
              currency: defaultCurrency,
              dc_indicator: isGain ? 'C' : 'D',
              line: 3,
              person: formValues.person,
            });
          }
        }
      }
    }

    return {
      description: formValues.description,
      date: formValues.date,
      partner_org_ind: formValues.partner_org_ind || null,
      partner: formValues.partner || null,
      contract: formValues.contract || null,
      transaction_items: transactionItems,
    };
  }, [accountCategoryMap, quantityLineInfo, defaultGainAccount, defaultLossAccount, defaultCurrency, getLockedCreditCashCurrency]);

  // ============================================
  // RENDER HELPERS
  // ============================================

  const renderPositionField = (line) => {
    const positionValue = values[`${line}_position_link`];
    const positionOptions = line === 'debit' ? debitPositionOptions : creditPositionOptions;

    return (
      <StyledSelect
        label={tr('Position-ID')}
        optionDefaultValue={tr('Choose Position')}
        name={`${line}_position_link`}
        value={positionValue}
        onChange={(e) => handlePositionChange(line, e)}
        onBlur={handleBlur}
        localeOptions={positionOptions}
        trObj={trObj}
        showRequiredAsterisk
        error={businessErrors[`${line}Position`]}
      />
    );
  };

  const renderAmountFields = (line) => {
    const isLocked = line === 'debit' ? amountLockState.debitLocked : amountLockState.creditLocked;
    const showQuantity = quantityLineInfo.line === line;
    const selectedAccounts = [
      values.debit_financial_account,
      values.credit_financial_account,
    ];
    const hideRepeatedCurrencyField = (
      line === 'credit' &&
      selectedAccounts.includes('cash') &&
      selectedAccounts.includes('bank')
    );
    const label = line === 'debit' ? tr('Amount (Received)') : tr('Amount (Payed)');

    return (
      <>
        <AmountRowWrapper>
          {hideRepeatedCurrencyField ? (
            <SingleAmountField>
              <StyledInput
                label={showQuantity ? `${label} / ${tr('Quantity')}` : label}
                name={`${line}_amount`}
                value={values[`${line}_amount`]}
                onChange={(e) => handleAmountChange(line, e)}
                onBlur={handleBlur}
                showRequiredAsterisk
                disabled={isLocked}
                fieldType="number"
                error={errors?.[`${line}_amount`]?.[0]}
              />
            </SingleAmountField>
          ) : (
            <AmountFieldPair>
              <StyledInput
                label={showQuantity ? `${label} / ${tr('Quantity')}` : label}
                name={`${line}_amount`}
                value={values[`${line}_amount`]}
                onChange={(e) => handleAmountChange(line, e)}
                onBlur={handleBlur}
                showRequiredAsterisk
                disabled={isLocked}
                fieldType="number"
                error={errors?.[`${line}_amount`]?.[0]}
              />
              <StyledSelect
                label={tr('Currency')}
                optionDefaultValue={tr('Currency')}
                name={`${line}_currency`}
                value={values[`${line}_currency`]}
                onChange={(e) => handleCurrencyChange(line, e)}
                onBlur={handleBlur}
                localeOptions={currencyLocaleOptions}
                trObj={trObj}
                disabled={isCurrencyLocked(line)}
              />
            </AmountFieldPair>
          )}

          <AmountFieldPair>
            <StyledInput
              label={tr('Amount in Space Currency')}
              name={`${line}_amount_sc`}
              value={values[`${line}_amount_sc`]}
              disabled
            />
            <StyledSelect
              label={tr('Space Currency')}
              name={`${line}_space_currency`}
              value={defaultCurrency}
              localeOptions={currencyLocaleOptions}
              trObj={trObj}
              disabled
            />
          </AmountFieldPair>
        </AmountRowWrapper>

        {showQuantity && (
          <DualInputWrapper>
            <StyledInput
              label={tr('Quantity')}
              name="quantity"
              value={values.quantity}
              onChange={handleChange}
              onBlur={handleBlur}
              fieldType="number"
              showRequiredAsterisk
              error={businessErrors.quantity}
            />
            <StyledInput
              label={tr('Unit')}
              value={getMeasurementUnitLabel(quantityLineInfo.measurementUnit)}
              disabled
            />
          </DualInputWrapper>
        )}
      </>
    );
  };

  // ============================================
  // RENDER
  // ============================================

  if (formLoading) {
    return <LottieLoader />;
  }

  return (
    <Wrapper className="form-wrapper">
      <div className="flex">
        <StyledButtonLink
          to={basePath}
          className="option-btn"
          icon="angle double left"
          name={tr('All Entries')}
        />
      </div>
      <div className="row">
        <div
          className="col-7 ml-10 mt-30 container"
          style={{
            position: 'relative',
            background: 'var(--admincat-color-grey-1)',
          }}
        >
          <TransactionForm
            onSubmit={handleSubmit}
            formData={values}
            title={tr('Asset Shift')}
            renderActionButtons={() => null}
            hideDefaultButtons={true}
            renderTitle={(props) => {
              const { title, tr, className, loadingData, detailMode, updateMode, handleEnterUpdateMode, handleEnterDetailMode } = props;
              return title ? (
                <div className={`card-header ${className}`}>
                  <h3 className="card-title" style={{ color: 'var(--admincat-color-grey-3)' }}>
                    {tr(title)}{' '}
                  </h3>
                  {loadingData && <span>Loading...</span>}
                  {(detailMode || updateMode) && (
                    <div style={{ marginLeft: 'auto', marginRight: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      {detailMode && (
                        <button onClick={handleEnterUpdateMode} type="button" className="ui button">
                          <i className="edit icon"></i>
                        </button>
                      )}
                      {updateMode && (
                        <button onClick={handleEnterDetailMode} type="button" className="ui button">
                          <i className="eye icon"></i>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : null;
            }}
          >
            {/* ... form content ... */}
            {/* Transaction Date */}
            <TopAlignedField>
              <StyledInputDatePicker
                label={tr('Transaction date')}
                name="date"
                value={values.date}
                onChange={handleChange}
                error={errors?.date?.[0]}
                showRequiredAsterisk
                maxDate={new Date()}
              />
            </TopAlignedField>

            {/* Description */}
            <TopAlignedField>
              <StyledInputDescription
                label={tr('Description')}
                name="description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                showRequiredAsterisk
                error={errors?.description?.[0]}
              />
            </TopAlignedField>

            <AlignedFormRows>
              {/* Person */}
              <StyledSelect
                label={tr('Person')}
                optionDefaultValue={tr('Choose Person')}
                name="person"
                value={values.person}
                onChange={handleChange}
                onBlur={handleBlur}
                options={personalInfoOptions}
                showRequiredAsterisk
                error={errors?.person?.[0]}
              />

              {/* Transaction Partner */}
              <DualInputWrapper>
                <StyledSelect
                  label={tr('Transaction Partner')}
                  optionDefaultValue={tr('Choose Organisation or Person')}
                  name="partner_org_ind"
                  value={values.partner_org_ind}
                  onChange={handlePartnerOrgIndChange}
                  localeOptions={orgIndicatorOptions}
                  sort={false}
                />
                {values.partner_org_ind && (
                  <StyledSelect
                    label={tr('Partner')}
                    optionDefaultValue={tr('Choose Partner')}
                    name="partner"
                    value={values.partner}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    localeOptions={organisationOrPersonOptions}
                  />
                )}
              </DualInputWrapper>

              {/* Contract / Item */}
              <DualInputWrapper>
                <StyledSelect
                  label={tr('Contract')}
                  optionDefaultValue={tr('Choose Contract')}
                  name="contract"
                  value={values.contract}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  localeOptions={contractOptions}
                />
                <StyledInput
                  label={tr('Item')}
                  name="contract_item"
                  value={values.contract_item}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </DualInputWrapper>

              {/* Accounting Section */}
              <SectionTitle>{tr('Accounting')}</SectionTitle>

              {/* Credit (C) Line */}
              <DualInputWrapper>
                <StyledSelect
                  label={
                    values.credit_financial_account === 'cash'
                      ? tr('Account (C)')
                      : tr('Account (C) / Position-ID')
                  }
                  optionDefaultValue={tr('Choose Account')}
                  name="credit_financial_account"
                  value={values.credit_financial_account}
                  onChange={(e) => handleAccountChange('credit', e)}
                  onBlur={handleBlur}
                  localeOptions={financialAccountLocaleOptions}
                  trObj={trObj}
                  showRequiredAsterisk
                  error={errors?.credit_financial_account?.[0]}
                />
                {values.credit_financial_account !== 'cash' && renderPositionField('credit')}
              </DualInputWrapper>

              {renderAmountFields('credit')}

              {/* Debit (D+) Line */}
              <DualInputWrapper>
                <StyledSelect
                  label={
                    values.debit_financial_account === 'cash'
                      ? tr('Account (D+)')
                      : tr('Account (D+) / Position-ID')
                  }
                  optionDefaultValue={tr('Choose Account')}
                  name="debit_financial_account"
                  value={values.debit_financial_account}
                  onChange={(e) => handleAccountChange('debit', e)}
                  onBlur={handleBlur}
                  localeOptions={financialAccountLocaleOptions}
                  trObj={trObj}
                  showRequiredAsterisk
                  error={errors?.debit_financial_account?.[0]}
                />
                {values.debit_financial_account !== 'cash' && renderPositionField('debit')}
              </DualInputWrapper>

              {renderAmountFields('debit')}
            </AlignedFormRows>

            {/* Business Errors */}
            {businessErrors.bankTransfer && (
              <ErrorMessage>{businessErrors.bankTransfer}</ErrorMessage>
            )}
            {businessErrors.cashBankCurrency && (
              <ErrorMessage>{businessErrors.cashBankCurrency}</ErrorMessage>
            )}
            {businessErrors.insufficientBalance && (
              <ErrorMessage>{businessErrors.insufficientBalance}</ErrorMessage>
            )}
            {businessErrors.amount && (
              <ErrorMessage>{businessErrors.amount}</ErrorMessage>
            )}

            {/* Submit Button - Conditional rendering based on mode */}
            {location.pathname.includes('create') && (
              <div className="row mt-4">
                <div className="col-md-12 d-flex">
                  <Button
                    style={{ width: '20%' }}
                    actionButton
                    type="submit"
                    name={tr('Post')}
                    disabled={isSubmitting || formLoading}
                    isLoading={isSubmitting}
                  />
                </div>
              </div>
            )}

            {/* Update mode only - Hide reverse button */}
            {updateMode && (
              <div className="row mt-4">
                <div className="col-md-12 d-flex">
                  <Button
                    style={{ width: '20%' }}
                    actionButton
                    type="submit"
                    name={tr('Save')}
                    disabled={isSubmitting || formLoading}
                    isLoading={isSubmitting}
                  />
                </div>
              </div>
            )}
          </TransactionForm>
        </div>
      </div>
    </Wrapper>
  );
};

AssetShiftForm.propTypes = {
  tr: PropTypes.func.isRequired,
  trObj: PropTypes.func,
  personalInfoOptions: PropTypes.array.isRequired,
  currencyLocaleOptions: PropTypes.array.isRequired,
  measurementLocaleOptions: PropTypes.array,
  userDefaultSpace: PropTypes.object.isRequired,
  gotoRoute: PropTypes.func.isRequired,
  basePath: PropTypes.string.isRequired,
  bsPosition: PropTypes.array,
  contractInfoOptions: PropTypes.array,
  setMsgInfo: PropTypes.func,
};


export default compose(
  withTranslation,
  withCurrency,
  withPersonalInfo,
  withUserDefaultSpace,
  withCustomRouter,
  withBSPosition,
  withMeasurementUnit,
  withContract,
  withMsgInfo,
)(AssetShiftForm);
